<template>
  <b-modal
    :visible="true"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    @close="$emit('close')"
    @hide="$emit('close')"
  >
    <template #modal-header>
      <h5 class="modal-title">
        {{ $t('microphoneGuide.header') }}
      </h5>
    </template>

    <div class="modal-body-content">
      <div class="icon-container">
        <svg class="mic-icon"
             viewBox="0 0 24 24"
             fill="none"
             stroke="currentColor"
        >
          <path stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
          <line x1="2"
                y1="2"
                x2="22"
                y2="22"
                stroke-width="2"
                stroke="currentColor"
          />
        </svg>
      </div>

      <p class="subtitle text-center">
        {{ $t('microphoneGuide.needAccess') }}
      </p>

      <b-alert show
               variant="danger"
               class="text-center"
      >
        {{ $t('microphoneGuide.disabledAlert') }}
      </b-alert>

      <div class="instructions">
        <h6 class="font-weight-bold">
          {{ $t('microphoneGuide.howToEnable') }}
        </h6>

        <!-- Chrome/Edge Instructions -->
        <div v-if="browser === 'chrome'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            {{ $t('microphoneGuide.chromeEdge') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.chromeEdgeStep1') }}</li>
            <li>{{ $t('microphoneGuide.chromeEdgeStep2') }}</li>
            <li>{{ $t('microphoneGuide.chromeEdgeStep3') }}</li>
            <li>{{ $t('microphoneGuide.chromeEdgeStep4') }}</li>
          </ol>
        </div>

        <!-- Firefox Instructions -->
        <div v-if="browser === 'firefox'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            {{ $t('microphoneGuide.firefox') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.firefoxStep1') }}</li>
            <li>{{ $t('microphoneGuide.firefoxStep2') }}</li>
            <li>{{ $t('microphoneGuide.firefoxStep3') }}</li>
          </ol>
        </div>

        <!-- Safari Instructions -->
        <div v-if="browser === 'safari'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            {{ $t('microphoneGuide.safari') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.safariStep1') }}</li>
            <li>{{ $t('microphoneGuide.safariStep2') }}</li>
            <li>{{ $t('microphoneGuide.safariStep3') }}</li>
            <li>{{ $t('microphoneGuide.safariStep4') }}</li>
          </ol>
        </div>

        <!-- Android Instructions -->
        <div v-if="browser === 'android'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            <i class="fab fa-android" /> {{ $t('microphoneGuide.android') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.androidStep1') }}</li>
            <li>{{ $t('microphoneGuide.androidStep2') }}</li>
            <ul class="sub-list">
              <li>{{ $t('microphoneGuide.androidSubStep1') }}</li>
              <li>{{ $t('microphoneGuide.androidSubStep2') }}</li>
              <li>{{ $t('microphoneGuide.androidSubStep3') }}</li>
              <li>{{ $t('microphoneGuide.androidSubStep4') }}</li>
            </ul>
            <li>{{ $t('microphoneGuide.androidStep3') }}</li>
          </ol>
        </div>

        <!-- iOS Instructions -->
        <div v-if="browser === 'ios'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            <i class="fab fa-apple" /> {{ $t('microphoneGuide.ios') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.iosStep1') }}</li>
            <li>{{ $t('microphoneGuide.iosStep2') }}</li>
            <ul class="sub-list">
              <li>{{ $t('microphoneGuide.iosSubStep1') }}</li>
              <li>{{ $t('microphoneGuide.iosSubStep2') }}</li>
              <li>{{ $t('microphoneGuide.iosSubStep3') }}</li>
              <li>{{ $t('microphoneGuide.iosSubStep4') }}</li>
            </ul>
            <li>{{ $t('microphoneGuide.iosStep3') }}</li>
          </ol>
        </div>

        <!-- Generic Instructions -->
        <div v-if="browser === 'other'"
             class="browser-guide"
        >
          <h6 class="text-info font-weight-bold">
            {{ $t('microphoneGuide.steps') }}
          </h6>
          <ol>
            <li>{{ $t('microphoneGuide.stepsStep1') }}</li>
            <li>{{ $t('microphoneGuide.stepsStep2') }}</li>
            <li>{{ $t('microphoneGuide.stepsStep3') }}</li>
            <li>{{ $t('microphoneGuide.stepsStep4') }}</li>
          </ol>
        </div>
      </div>

      <b-collapse v-model="showTroubleshooting"
                  class="mt-3"
      >
        <h6 class="font-weight-bold mb-2">
          {{ $t('microphoneGuide.troubleshootHeader') }}
        </h6>
        <ul class="troubleshooting-list">
          <li>{{ $t('microphoneGuide.troubleStep1') }}</li>
          <li>{{ $t('microphoneGuide.troubleStep2') }}</li>
          <li>{{ $t('microphoneGuide.troubleStep3') }}</li>
          <li>{{ $t('microphoneGuide.troubleStep4') }}</li>
          <li v-if="!isAndroid && !isIOS">
            {{ $t('microphoneGuide.troubleStep5') }}
          </li>
          <li v-if="isAndroid || isIOS">
            {{ $t('microphoneGuide.troubleStep6') }}
          </li>
          <li v-if="isAndroid">
            {{ $t('microphoneGuide.troubleStep7') }}
          </li>
          <li v-if="isIOS">
            {{ $t('microphoneGuide.troubleStep8') }}
          </li>
        </ul>
      </b-collapse>

      <b-button
        variant="link"
        size="sm"
        class="mt-3"
        @click="showTroubleshooting = !showTroubleshooting"
      >
        {{ showTroubleshooting ? $t('microphoneGuide.hideTroubleshooting') : $t('microphoneGuide.showTroubleshooting') }}
      </b-button>
    </div>

    <template #modal-footer>
      <b-button variant="secondary"
                @click="$emit('close')"
      >
        {{ $t('common.ok') }}
      </b-button>
      <b-button variant="primary"
                @click="requestMicPermission"
      >
        {{ $t('microphoneGuide.grantAccess') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BCollapse, BButton, BAlert,
} from 'bootstrap-vue'

export default {
  name: 'MicrophoneGuideModal',
  components: {
    BModal,
    BCollapse,
    BButton,
    BAlert,
  },

  data() {
    return {
      showTroubleshooting: false,
      browser: 'other',
      isAndroid: false,
      isIOS: false,
    }
  },

  mounted() {
    this.detectBrowser()
    this.detectDevice()
  },

  methods: {
    detectBrowser() {
      const ua = navigator.userAgent.toLowerCase()

      if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('android')) {
        this.browser = 'chrome'
      } else if (ua.includes('firefox')) {
        this.browser = 'firefox'
      } else if (ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android')) {
        this.browser = 'safari'
      } else if (ua.includes('edg')) {
        this.browser = 'chrome'
      } else if (ua.includes('android')) {
        this.browser = 'android'
      } else if (ua.includes('iphone') || ua.includes('ipad')) {
        this.browser = 'ios'
      } else {
        this.browser = 'other'
      }
    },

    detectDevice() {
      const ua = navigator.userAgent.toLowerCase()
      this.isAndroid = ua.includes('android')
      this.isIOS = ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')
    },

    async requestMicPermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(track => track.stop())
        this.$emit('close')
        this.$emit('permission-granted')
        this.$bvToast.toast(
          this.$t('microphoneGuide.toastGranted'),
          {
            title: this.$t('microphoneGuide.toastGrantedTitle'),
            variant: 'success',
            solid: true,
          },
        )
      } catch (error) {
        let message = this.$t('microphoneGuide.toastError.generic')

        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          message = this.$t('microphoneGuide.toastError.permissionDenied')
        } else if (error.name === 'NotFoundError') {
          message = this.$t('microphoneGuide.toastError.notFound')
        } else if (error.name === 'NotReadableError') {
          message = this.$t('microphoneGuide.toastError.notReadable')
        } else if (error.name === 'SecurityError') {
          message = this.$t('microphoneGuide.toastError.security')
        }

        this.$bvToast.toast(message, {
          title: this.$t('microphoneGuide.toastErrorTitle'),
          variant: 'danger',
          solid: true,
        })

        this.$emit('permission-denied', error)
      }
    },

    recheckPermission() {
      this.$emit('recheck-permission')
    },
  },
}
</script>

  <style scoped>
  .modal-body-content {
    padding: 20px 0;
  }

  .icon-container {
    text-align: center;
    margin-bottom: 20px;
  }

  .mic-icon {
    width: 80px;
    height: 80px;
    color: #dc3545;
    stroke-width: 1.5;
  }

  .subtitle {
    font-size: 16px;
    color: #6c757d;
    margin-bottom: 20px;
  }

  .instructions {
    margin: 20px 0;
  }

  .instructions h6 {
    margin-bottom: 15px;
    color: #212529;
  }

  .browser-guide {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    border-left: 4px solid #17a2b8;
    margin-bottom: 15px;
  }

  .browser-guide h6 {
    margin-bottom: 10px;
    font-size: 14px;
  }

  .browser-guide ol {
    margin: 0;
    padding-left: 20px;
  }

  .browser-guide li {
    margin-bottom: 8px;
    line-height: 1.6;
    color: #495057;
    font-size: 14px;
  }

  .browser-guide strong {
    color: #212529;
    font-weight: 600;
  }

  .sub-list {
    margin: 8px 0 8px 20px;
    padding-left: 20px;
    list-style-type: circle;
  }

  .sub-list li {
    margin-bottom: 6px;
    color: #495057;
    font-size: 14px;
  }

  .troubleshooting-list {
    margin: 10px 0;
    padding-left: 20px;
  }

  .troubleshooting-list li {
    margin-bottom: 8px;
    color: #6c757d;
    line-height: 1.6;
    font-size: 14px;
  }
  </style>
